import { ValidationRules, validationPatterns } from '../../shared/validation';

export interface FieldConfig {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'date' | 'select' | 'textarea' | 'checkbox';
  options?: string[];
  relation?: 'workers' | 'machines' | 'operations' | 'materials' | 'assignments';
  optionLabel?: (item: any) => string;
  readonly?: boolean;
  required?: boolean;
  superadminOnly?: boolean;
  validation?: ValidationRules;
}

export interface ResourceConfig {
  title: string;
  endpoint: string;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  fields: FieldConfig[];
  columns: TableColumn[];
}

export interface TableColumn {
  field?: string;
  headerName: string;
  valueGetter?: (params: any) => string | number;
  valueFormatter?: (params: any) => string;
}

/**
 * Creates a value getter for populated reference fields.
 * @param field Row field that may contain an object reference.
 * @param property Property to display from the populated object.
 * @returns Table value getter function.
 */
const ref = (field: string, property: string) => (params: any) => params.data?.[field]?.[property] || params.data?.[field] || '';

/**
 * Builds a readable assignment summary for production table rows.
 * @param params Material table callback parameters.
 * @returns Human-readable assignment label.
 */
const assignmentDetails = (params: any) => {
  const assignment = params.data?.assignment;
  if (!assignment || typeof assignment !== 'object') return assignment || '';
  const worker = assignment.worker?.name || 'Worker';
  const machine = assignment.machine?.machineId || 'Machine';
  const operation = assignment.operation?.operationName || 'Operation';
  const material = assignment.material?.materialNumber || 'Material';
  const target = assignment.quantityToProduce || 0;
  return `${worker} - ${machine} - ${operation} - ${material} - Target ${target}`;
};
/**
 * Formats a date/time cell using Indian locale conventions.
 * @param params Material table callback parameters.
 * @returns Localized date/time label.
 */
const dateTime = (params: any) =>
  params.value ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(params.value)) : '';

/**
 * Formats a date-only cell using Indian locale conventions.
 * @param params Material table callback parameters.
 * @returns Localized date label.
 */
const dateOnly = (params: any) =>
  params.value ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(params.value)) : '';

const rawDeviceUserId = (params: any) => params.data?.raw?.deviceUserId || params.data?.employeeId || '';
const rawRecordTime = (params: any) => params.data?.raw?.recordTime || params.data?.time || '';

export const RESOURCE_CONFIG: Record<string, ResourceConfig> = {
  workers: {
    title: 'Employees',
    endpoint: '/workers',
    canCreate: true,
    canEdit: true,
    canDelete: true,
    fields: [
      { key: 'name', label: 'Name', required: true, validation: { kind: 'text', minLength: 2, maxLength: 80, pattern: validationPatterns.safeText } },
      { key: 'employeeId', label: 'Employee ID', required: true, validation: { kind: 'text', minLength: 2, maxLength: 30, pattern: validationPatterns.code } },
      { key: 'shift', label: 'Shift', type: 'select', options: ['Morning', 'Night'], required: true },
      { key: 'department', label: 'Department', required: true, validation: { kind: 'text', minLength: 2, maxLength: 60, pattern: validationPatterns.safeText } },
      { key: 'skillLevel', label: 'Skill Level', type: 'select', options: ['Trainee', 'Skilled', 'Expert'], required: true },
      { key: 'description', label: 'Description', type: 'select', options: ['Operator', 'Sr. Operator', 'Team Lead'], required: true },
      { key: 'dateOfJoining', label: 'Date of Joining', type: 'date', required: true },
      // { key: 'active', label: 'Active', type: 'checkbox' }
    ],
    columns: [
      { field: 'name', headerName: 'Name' },
      { field: 'employeeId', headerName: 'Employee ID' },
      { field: 'shift', headerName: 'Shift' },
      { field: 'department', headerName: 'Department' },
      { field: 'skillLevel', headerName: 'Skill Level' },
      { field: 'description', headerName: 'Description' },
      { field: 'dateOfJoining', headerName: 'Date of Joining', valueFormatter: dateTime },
      // { field: 'active', headerName: 'Active' }
    ]
  },
  machines: {
    title: 'Machine Register',
    endpoint: '/machines',
    canCreate: true,
    canEdit: true,
    canDelete: true,
    fields: [
      { key: 'machineId', label: 'Machine ID', required: true, validation: { kind: 'text', minLength: 2, maxLength: 30, pattern: validationPatterns.code } },
      { key: 'machineType', label: 'Machine Type', required: true, validation: { kind: 'text', minLength: 2, maxLength: 60, pattern: validationPatterns.safeText } },
      { key: 'department', label: 'Department', required: true, validation: { kind: 'text', minLength: 2, maxLength: 60, pattern: validationPatterns.safeText } },
      { key: 'status', label: 'Status', type: 'select', options: ['Active', 'Under Maintenance', 'Retired'], required: true },
      // { key: 'active', label: 'Active', type: 'checkbox' }
    ],
    columns: [
      { field: 'machineId', headerName: 'Machine ID' },
      { field: 'machineType', headerName: 'Machine Type' },
      { field: 'department', headerName: 'Department' },
      { field: 'status', headerName: 'Status' },
      // { field: 'active', headerName: 'Active' }
    ]
  },
  operations: {
    title: 'Operation Control',
    endpoint: '/operations',
    canCreate: true,
    canEdit: true,
    canDelete: true,
    fields: [
      { key: 'machine', label: 'Machine', relation: 'machines', optionLabel: (item) => `${item.machineId} - ${item.machineType}`, required: true },
      { key: 'operationName', label: 'Operation Name', required: true, validation: { kind: 'text', minLength: 2, maxLength: 80, pattern: validationPatterns.safeText } },
      { key: 'operationCategory', label: 'Operation Category', type: 'select', options: ['Production', 'Assembly', 'QC', 'Finishing'], required: true },
      { key: 'setupTime', label: 'Setup Time', type: 'number', superadminOnly: true, validation: { kind: 'number', min: 0, max: 1440 } },
      { key: 'cycleTime', label: 'Cycle Time', type: 'number', superadminOnly: true, validation: { kind: 'number', min: 0, max: 1440 } }
    ],
    columns: [
      { headerName: 'Machine', valueGetter: ref('machine', 'machineId') },
      { field: 'operationName', headerName: 'Operation' },
      { field: 'operationCategory', headerName: 'Category' },
      { field: 'setupTime', headerName: 'Setup Time' },
      { field: 'cycleTime', headerName: 'Cycle Time' },
      // { field: 'locked', headerName: 'Locked' }
    ]
  },
  materials: {
    title: 'Material QR Stock',
    endpoint: '/materials',
    canCreate: true,
    canEdit: false,
    canDelete: false,
    fields: [
      { key: 'materialNumber', label: 'Material Number', required: true, validation: { kind: 'text', minLength: 2, maxLength: 40, pattern: validationPatterns.code } },
      { key: 'description', label: 'Description', required: true, validation: { kind: 'text', minLength: 2, maxLength: 160, pattern: validationPatterns.safeText } },
      { key: 'quantity', label: 'Total Quantity', type: 'number', required: true, validation: { kind: 'number', min: 0, max: 10000000 } }
    ],
    columns: [
      { field: 'materialNumber', headerName: 'Material Number' },
      { field: 'description', headerName: 'Description' },
      { headerName: 'Total Quantity', valueGetter: (params) => params.data?.totalQuantity ?? params.data?.quantity ?? 0 },
      { headerName: 'Available Quantity', valueGetter: (params) => params.data?.availableQuantity ?? params.data?.quantity ?? 0 },
      { headerName: 'Rejected Quantity', valueGetter: (params) => params.data?.rejectedQuantity ?? 0 },
      // { field: 'readOnly', headerName: 'Read Only' },
      { field: 'createdAt', headerName: 'Added Date/Time', valueFormatter: dateTime }
    ]
  },
  holidays: {
    title: 'Holiday Calendar',
    endpoint: '/holidays',
    canCreate: true,
    canEdit: true,
    canDelete: true,
    fields: [
      { key: 'date', label: 'Date', type: 'date', required: true, validation: { kind: 'date', noPastDate: true } },
      { key: 'dayType', label: 'Day Type', type: 'select', options: ['Working Day', 'Holiday', 'Half Day'], required: true },
      { key: 'name', label: 'Name', validation: { kind: 'text', maxLength: 80, pattern: validationPatterns.safeText } },
      { key: 'notes', label: 'Notes', type: 'textarea', validation: { kind: 'textarea', maxLength: 300 } },
      { key: 'active', label: 'Active', type: 'checkbox' }
    ],
    columns: [
      { field: 'date', headerName: 'Date', valueFormatter: dateOnly },
      { field: 'dayType', headerName: 'Day Type' },
      { field: 'name', headerName: 'Name' },
      { field: 'notes', headerName: 'Notes' },
      { field: 'active', headerName: 'Active' }
    ]
  },
  assignments: {
    title: 'Work Assignment',
    endpoint: '/assignments',
    canCreate: true,
    canEdit: true,
    canDelete: true,
    fields: [
      { key: 'worker', label: 'Worker', relation: 'workers', optionLabel: (item) => `${item.name} (${item.employeeId})`, required: true },
      { key: 'machine', label: 'Machine', relation: 'machines', optionLabel: (item) => `${item.machineId} - ${item.machineType}`, required: true },
      { key: 'operation', label: 'Operation', relation: 'operations', optionLabel: (item) => `${item.operationName}${item.locked ? ' - Locked' : ''}`, required: true },
      { key: 'material', label: 'Material', relation: 'materials', optionLabel: (item) => `${item.materialNumber} - Qty ${item.quantity}`, required: true },
      { key: 'quantityToProduce', label: 'Quantity To Produce', type: 'number', required: true, validation: { kind: 'number', min: 1, max: 1000000, integer: true } },
      { key: 'scheduledFor', label: 'Scheduled For', type: 'date', required: true, validation: { kind: 'date', noPastDate: true } }
    ],
    columns: [
      { headerName: 'Worker', valueGetter: ref('worker', 'name') },
      { headerName: 'Shift', valueGetter: ref('worker', 'shift') },
      { headerName: 'Machine', valueGetter: ref('machine', 'machineId') },
      { headerName: 'Operation', valueGetter: ref('operation', 'operationName') },
      { headerName: 'Material', valueGetter: ref('material', 'materialNumber') },
      { field: 'quantityToProduce', headerName: 'Target' },
      { field: 'calendarDayType', headerName: 'Day Type' },
      { headerName: 'Work Hours', valueGetter: (params) => Number(params.data?.availableWorkMinutes || 540) / 60 },
      { field: 'status', headerName: 'Status' },
      { field: 'scheduledFor', headerName: 'Scheduled For', valueFormatter: dateTime }
    ]
  },
  attendance: {
    title: 'Attendance Events',
    endpoint: '/attendance-events',
    canCreate: false,
    canEdit: false,
    canDelete: false,
    fields: [],
    columns: [
      { field: 'sourceRowId', headerName: 'Source Row' },
      { headerName: 'Device User ID', valueGetter: rawDeviceUserId },
      { field: 'checkType', headerName: 'Derived Check Type' },
      { headerName: 'Record Time', valueGetter: rawRecordTime, valueFormatter: dateTime },
      { field: 'status', headerName: 'Status' }
    ]
  },
  production: {
    title: 'Production Entries',
    endpoint: '/production-entries',
    canCreate: true,
    canEdit: true,
    canDelete: false,
    fields: [
      { key: 'assignment', label: 'Today Assignment', relation: 'assignments', optionLabel: (item) => `${item.worker?.name || 'Worker'} - ${item.machine?.machineId || 'Machine'} - Target ${item.quantityToProduce}`, required: true },
      { key: 'producedQuantity', label: 'Produced Quantity', type: 'number', required: true, validation: { kind: 'number', min: 0, max: 1000000, integer: true } },
      { key: 'rejectedQuantity', label: 'Rejected Quantity', type: 'number', required: true, validation: { kind: 'number', min: 0, max: 1000000, integer: true } },
      { key: 'rejectionReason', label: 'Rejection Reason', type: 'select', options: ['Material Defect', 'Machine Issue', 'Dimension Mismatch', 'Surface Damage', 'Assembly Error', 'Quality Check Failed'] },
      { key: 'reason', label: 'Reason', type: 'textarea', validation: { kind: 'textarea', maxLength: 300 } }
    ],
    columns: [
      { headerName: 'Worker', valueGetter: ref('worker', 'name') },
      { headerName: 'Assignment', valueGetter: assignmentDetails },
      { field: 'producedQuantity', headerName: 'Produced' },
      { field: 'rejectedQuantity', headerName: 'Rejected' },
      { field: 'rejectionReason', headerName: 'Rejection Reason' },
      { field: 'reason', headerName: 'Reason' }
    ]
  },
  approvals: {
    title: 'Pending Approvals',
    endpoint: '/approvals/pending',
    canCreate: false,
    canEdit: false,
    canDelete: false,
    fields: [],
    columns: [
      { headerName: 'Worker', valueGetter: ref('worker', 'name') },
      { headerName: 'Date', valueGetter: (params) => params.data?.assignment?.scheduledFor || params.data?.updatedAt || '', valueFormatter: dateOnly },
      { headerName: 'Target', valueGetter: (params) => params.data?.assignment?.quantityToProduce || '' },
      { headerName: 'Produced', valueGetter: (params) => params.data?.productionEntry?.producedQuantity || 0 },
      { headerName: 'Rejected', valueGetter: (params) => params.data?.productionEntry?.rejectedQuantity || 0 },
      { headerName: 'Rejection Reason', valueGetter: (params) => params.data?.productionEntry?.rejectionReason || '' },
      { headerName: 'Reason', valueGetter: (params) => params.data?.productionEntry?.reason || '' },
      { field: 'currentMonthTargetMissCount', headerName: 'Monthly Miss Count' },
      { field: 'status', headerName: 'Status' },
      { field: 'notes', headerName: 'Notes' }
    ]
  },
  approvalLog: {
    title: 'Approval Log',
    endpoint: '/approvals',
    canCreate: false,
    canEdit: false,
    canDelete: false,
    fields: [],
    columns: [
      { headerName: 'Worker', valueGetter: ref('worker', 'name') },
      { headerName: 'Employee ID', valueGetter: ref('worker', 'employeeId') },
      { headerName: 'Approved By', valueGetter: ref('approver', 'name') },
      { field: 'approverRole', headerName: 'Approver Role' },
      { field: 'decision', headerName: 'Decision' },
      // { field: 'approvalCount', headerName: 'Approval Count' },
      { field: 'approvalMonth', headerName: 'Approval Month' },
      { field: 'notes', headerName: 'Approval Reason' },
      { field: 'createdAt', headerName: 'Approved At', valueFormatter: dateTime }
    ]
  },
  materialUsage: {
    title: 'Material Usage Report',
    endpoint: '/reports/material-usage',
    canCreate: false,
    canEdit: false,
    canDelete: false,
    fields: [],
    columns: [
      { headerName: 'Material', valueGetter: ref('material', 'materialNumber') },
      { headerName: 'Worker', valueGetter: ref('worker', 'name') },
      { headerName: 'Machine', valueGetter: ref('machine', 'machineId') },
      { headerName: 'Operation', valueGetter: ref('operation', 'operationName') },
      { field: 'quantityUsed', headerName: 'Quantity Used' },
      { field: 'usedAt', headerName: 'Used At', valueFormatter: dateTime }
    ]
  }
};
